import { useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import { Switch, SwitchProps } from '@cast/design-system';
import { WorkloadOptimizationState } from '@cast/types';

import {
  isOptimizationRightsizingOverridden,
  PreparedWorkload,
} from 'features/workload-optimization';
import { useUpdateWorkloadMutation } from 'hooks/mutations/workload-optimization';

import { useIsConfirmationRequiredOnToggleOptimization } from '../_hooks/permissions/useIsConfirmationRequiredOnToggleOptimization';
import { useWorkloadOptimizationChangeConfirmationDialogs } from '../_hooks/useWorkloadOptimizationChangeConfirmationDialogs';
import { workloadOverridesToVpaConfigApiModel } from '../_utils';

type Props = {
  workload: PreparedWorkload;
} & SwitchProps;

export const VpaOptimizationSwitch = ({
  workload,
  disabled: _disabled,
  ...rest
}: Props) => {
  const { mutate, isPending } = useUpdateWorkloadMutation();
  const [isChecked, setIsChecked] = useState(workload.isVerticalScalingEnabled);
  const [prevChecked, setPrevChecked] = useState(
    workload.isVerticalScalingEnabled
  );
  if (prevChecked !== workload.isVerticalScalingEnabled) {
    setIsChecked(workload.isVerticalScalingEnabled);
    setPrevChecked(workload.isVerticalScalingEnabled);
  }
  const {
    openOverrideOptimizationStatusConfirmationDialog,
    openRevertToPolicyStatusConfirmationDialog,
  } = useWorkloadOptimizationChangeConfirmationDialogs();
  const { isConfirmationRequired } =
    useIsConfirmationRequiredOnToggleOptimization();

  const disabled =
    _disabled ||
    isPending ||
    workload.verticalScalingPolicy?.isCastware ||
    workload.isManagedByAnnotations;

  const { isVpaOverridden } = isOptimizationRightsizingOverridden(
    workload.workloadOverrides
  );

  const onChange = (_: unknown, checked: boolean) => {
    setIsChecked(checked);

    const currentVpa = cloneDeep(
      workloadOverridesToVpaConfigApiModel(workload.workloadOverrides)
    );

    currentVpa.managementOption = checked
      ? WorkloadOptimizationState.MANAGED
      : WorkloadOptimizationState.READ_ONLY;

    mutate(
      {
        workloadId: workload.id,
        clusterId: workload.clusterId,
        workload: {
          scalingPolicyId: workload.scalingPolicyId,
          workloadConfig: {
            vpaConfig: currentVpa,
            hpaConfig: workload.workloadOverrides?.horizontal,
          },
        },
      },
      {
        onError: () => {
          setIsChecked(workload.isVerticalScalingEnabled);
        },
      }
    );
  };

  return (
    <Switch
      checked={isChecked}
      disabled={disabled}
      onChange={
        isConfirmationRequired
          ? (_: unknown, checked: boolean) => {
              setIsChecked(checked);
              if (isVpaOverridden) {
                openRevertToPolicyStatusConfirmationDialog({
                  willBeEnabled: checked,
                  onCancel: () => setIsChecked((p) => !p),
                  onConfirm: () => onChange(_, checked),
                });
              } else {
                openOverrideOptimizationStatusConfirmationDialog({
                  willBeEnabled: checked,
                  onCancel: () => setIsChecked((p) => !p),
                  onConfirm: () => onChange(_, checked),
                });
              }
            }
          : onChange
      }
      size="sm"
      onClick={(event) => {
        event.stopPropagation();
      }}
      {...rest}
    />
  );
};
